/**
 * Focus Visible Polyfill created for Skapa Web Components
 *
 * The advantage of this implementation compared to the well known 'focus-visible' package is
 * that it automatically penetrates the shadowDom by using the event's composedPath method.
 * It sets the 'focus-visible' class on the interactive element
 * which has been focused by keyboard navigation.
 *
 * It automatically detects if the 'focus-visible' polyfill has been loaded, and will not
 * interfere with its behaviour, by setting itself to `shadowDomOnly` mode.
 *
 * It can also be imported multiple times and will not create duplicate instances.
 *
 * This implementation will not set the ".focus-visible" class on input elements and textareas
 * which have been interacted with via mouse.
 */
const INITIALIZED_FLAG = 'skapa-shadow-focus-feature';
/** Helper flag, prevents multiple initialization. */
let inited = false;
/** Helper flag, true if mouse is down. */
let isMouseDown = false;
/** If true, the polyfill will only apply to elements within shadow doms. */
let shadowDomOnly = false;
/** Stores if the last interaction with the page used keyboard. All focus events will be considered keyboard controlled as long as the value is true. */
let hadKeyboardEvent = false;
// For testing and benchmarking
let listenerCount = 0;
const activeShadowDoms = [];
function handleKey(event) {
    if (!(event.ctrlKey || event.altKey || event.metaKey)) {
        hadKeyboardEvent = true;
        if (event.type === 'keydown') {
            const target = event.composedPath()[0];
            if (['input', 'textarea'].includes(target.tagName.toLowerCase()) || target.hasAttribute('tabIndex') && target.tabIndex >= 0) {
                fVisible([target], true);
            }
        }
    }
}
function purgeAllFocus() {
    activeShadowDoms.map(ignoreFocusEvents);
}
function handleMouse(event) {
    hadKeyboardEvent = false;
    isMouseDown = event.type === 'pointerdown'; // pointerTypes.includes(event.type);
    purgeAllFocus();
    // The nested event listener chain has to be updated on pointer induced focus shift.
    const path = event.composedPath();
    const shadows = getShadowDomsFromChain(path[0]);
    shadows.map(listenForFocusEvents);
}
function fVisible(elements, on = true) {
    // add checks and conversions here
    if (elements) {
        elements.forEach(element => element.classList.toggle('focus-visible', on));
    }
}
function getShadowDomsFromChain(leafNode) {
    // the path will not point down to the document, only until the listener that catched it.
    // so we manually need to create the chain
    const result = []; // path.filter(element => (element.nodeType === 11) && (element !== document)) as Array<ShadowRoot>;
    let leaf = leafNode;
    let shadow;
    while (((shadow = leaf.getRootNode()) !== document) && (shadow.nodeType === 11)) {
        result.push(shadow);
        leaf = shadow.host;
    }
    return result;
}
function listenForFocusEvents(shadow) {
    shadow.addEventListener('focusin', microHandleFocus);
    shadow.addEventListener('focusout', microHandleFocus);
    activeShadowDoms.push(shadow);
    listenerCount += 2;
}
function ignoreFocusEvents(shadow) {
    shadow.removeEventListener('focusin', microHandleFocus);
    shadow.removeEventListener('focusout', microHandleFocus);
    activeShadowDoms.splice(activeShadowDoms.indexOf(shadow), 1);
    listenerCount -= 2;
}
/** Called whenever there has been a focus shift dispatched.
 * However, focus shifts within a shadow DOM will not leave the shadowDom.
 * Which means that if a shadowDom has multiple interactive elements, the document event listener will never know.
 *
 * The solution is to add/remove an event listener to each shadowDom that connects the document with the nested (final) event target.
 */
function handleFocus(event) {
    const eventChainTip = event.path[0];
    if (event.type === 'focusin') {
        // focusing in on new element
        const shadows = getShadowDomsFromChain(event.path[0]);
        const newShadows = shadows.filter(e => !activeShadowDoms.includes(e));
        const garbageShadows = activeShadowDoms.filter(e => !shadows.includes(e));
        // This removes the out of scope listeners created in the previous focus-in.
        garbageShadows.map(ignoreFocusEvents);
        // Adds listeners to the shadowDoms that are part of the new branch
        newShadows.map(listenForFocusEvents);
        // Note: Do not merge this condition with the parent condition.
        if (hadKeyboardEvent && !isMouseDown) {
            // show focus visible
            if ((shadowDomOnly && shadows.length) // if it is in shadowDomOnly mode then it checks if the tip of the event chain is inside at least one shadowDom.
                || !shadowDomOnly // Or if it is not shadowDomOnly mode.
            )
                fVisible([eventChainTip], true); // Then apply the `focus-visible` class.
        }
    }
    else {
        // focusing out
        // remove focus visible form the current
        fVisible([eventChainTip], false);
        // The listeners will be managed on the new focusin, unless there is no new focusin.
        if (!event.relatedTarget) {
            // no new focus, remove all listeners except root;
            purgeAllFocus();
        }
    }
}
// Non debouncing micro wrapper
const micro = (callback) => (event) => {
    // Potentially some checks needs to be made to ignore focus events artifically dispatched by non DOM elements.. Not even sure if its possible
    // Need to save the event path and some details as they might not be available later.
    const duplicateEvent = { relatedTarget: event.relatedTarget, target: event.target, type: event.type, path: event.composedPath() };
    setTimeout(() => callback(duplicateEvent));
};
const microHandleFocus = micro(handleFocus);
export default function applyFocusVisible(listenerBase) {
    // TODO: Add a test if the environment supports :focus-visible, then probably do nothing.
    // However, need to check how :focus-visible behaves with custom elements.
    // Does the :host also get the pseudo class or only the interactive element?
    // If already initalized then do nothing;
    if (inited)
        return;
    // If focus-visible polyfill is present in the document then just extend the feature into the shadowDom;
    if ((typeof window !== 'undefined') && window.hasOwnProperty('applyFocusVisiblePolyfill'))
        shadowDomOnly = true;
    if (!shadowDomOnly) {
        window.addEventListener('focus-visible-polyfill-ready', // Based on: https://github.com/WICG/focus-visible#lazy-loading
        () => shadowDomOnly = true, // Make sure that the tool sets itself to complementary mode if the conventional polyfill is loaded.
        { once: true });
    }
    listenerBase = listenerBase || document.documentElement; // Potentially need to check with some SSR render environments
    // This polyfill has been already loaded from a different source. Exit
    if (listenerBase.hasAttribute(INITIALIZED_FLAG))
        return;
    // Register key state
    listenerBase.addEventListener('keydown', handleKey);
    listenerBase.addEventListener('keyup', handleKey);
    // Register mouse or input state, pointer interaction will clear all focus visible class declarations additions.
    listenerBase.addEventListener('pointerdown', handleMouse);
    listenerBase.addEventListener('pointerup', handleMouse);
    listenerBase.addEventListener('drag', handleMouse); // required to disable on accidental drag events that may not fire pointerup properly.
    // These needs to be wrapped inside micro tasks to synchronise keyboard state check when calling `.focus()` before the keydown listener triggers here.
    listenerBase.addEventListener('focusin', microHandleFocus);
    listenerBase.addEventListener('focusout', microHandleFocus);
    if (!listenerBase.toggleAttribute) {
        // Some virtual environments don't support toggleAttribute
        listenerBase.toggleAttribute = (qualifiedName, force) => {
            if (force)
                listenerBase === null || listenerBase === void 0 ? void 0 : listenerBase.setAttribute(qualifiedName, '');
            else
                listenerBase === null || listenerBase === void 0 ? void 0 : listenerBase.removeAttribute(qualifiedName);
            return Boolean(force);
        };
    }
    // Mark as initialized
    listenerBase.toggleAttribute(INITIALIZED_FLAG, true);
    listenerBase.classList.add('js-focus-visible'); // This will enable compatiblity with styles targeted for the `focus-visible` package.
    inited = true;
}
